import { useState } from 'react';
import type { Report, ReportArtist, InfrastructureEntry } from '@tastymusic/research-agent';
import { BArtistCard } from './BArtistCard';
import { BClickableArtistName } from '../BClickableArtistName';
import { PlatformLinks, type AnyLinks } from '../../../components/PlatformLinks';
import { useArtistData } from '../../../context/ArtistDataContext';
import './BDeepDive.css';

// Artist type from BResultsPage (discovered artists)
interface DiscoveredArtist {
  name: string;
  genres?: string[];
  era?: string | null;
  location?: string | null;
  matchReason?: string;
  confidence?: number;
  tracks?: string[];
  relatedArtists?: string[];
  links?: {
    bandcamp?: string;
    spotify?: string;
    youtube?: string;
  };
  youtubeVideoId?: string;
  bandcampEmbed?: {
    embedHtml: string;
    albumId?: string;
    title?: string;
    bandcampUrl?: string;
  };
}

interface BDeepDiveProps {
  report: Report | null;
  artists: DiscoveredArtist[];
  isBuilding?: boolean;
  buildingTab?: TabId;
  onArtistClick?: (artistName: string) => void;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
}

export type TabId = 'genre' | 'scenes' | 'mutations' | 'more' | 'infrastructure' | 'coda' | 'reference';

const TABS: { id: TabId; label: string }[] = [
  { id: 'more', label: 'More Artists' },
  { id: 'genre', label: 'Genre' },
  { id: 'scenes', label: 'Scenes' },
  { id: 'mutations', label: 'Mutations' },
  { id: 'infrastructure', label: 'Infrastructure' },
  { id: 'coda', label: 'Coda' },
  { id: 'reference', label: 'Reference' },
];

/**
 * Deep dive section with expandable tabs.
 */
export function BDeepDive({
  report,
  artists,
  isBuilding = false,
  buildingTab,
  onArtistClick,
  expanded,
  onExpandedChange,
}: BDeepDiveProps) {
  const [internalExpanded, setInternalExpanded] = useState(true);
  const [activeTab, setActiveTab] = useState<TabId>('more');

  // Support both controlled and uncontrolled modes
  const isExpanded = expanded !== undefined ? expanded : internalExpanded;
  const setIsExpanded = (value: boolean) => {
    if (onExpandedChange) {
      onExpandedChange(value);
    } else {
      setInternalExpanded(value);
    }
  };

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
    if (!isExpanded) {
      // Scroll into view when expanding, accounting for fixed header
      setTimeout(() => {
        const element = document.getElementById('b-deep-content');
        if (element) {
          const headerHeight = 80;
          const elementPosition = element.getBoundingClientRect().top + window.scrollY;
          window.scrollTo({ top: elementPosition - headerHeight, behavior: 'smooth' });
        }
      }, 100);
    }
  };

  // Determine which tabs have content
  const hasGenre = !!(report?.introduction?.content || report?.founders?.founders?.length || report?.founders?.parentGenres?.length);
  const hasScenes = !!(report?.scenes && report.scenes.length > 0);
  const hasMutations = !!(report?.mutations && report.mutations.length > 0);
  const hasMore = !!(report?.moreArtists?.artists?.length || artists.length > 6);
  const hasInfra = !!(report?.infrastructure && (report.infrastructure.labels?.length || report.infrastructure.radio?.length || report.infrastructure.festivals?.length));
  const hasCoda = !!report?.coda?.content;
  const hasReference = !!(report?.quickReference && report.quickReference.length > 0);

  const availableTabs = TABS.filter(tab => {
    switch (tab.id) {
      case 'genre': return hasGenre;
      case 'scenes': return hasScenes;
      case 'mutations': return hasMutations;
      case 'more': return hasMore;
      case 'infrastructure': return hasInfra;
      case 'coda': return hasCoda;
      case 'reference': return hasReference;
      default: return false;
    }
  });

  // Don't render anything until at least one tab has content
  if (availableTabs.length === 0) {
    return null;
  }

  return (
    <section className="b-deep-dive">
      {/* Trigger button */}
      <div className="b-deep-dive__trigger">
        <button className="b-deep-dive__btn" onClick={toggleExpand}>
          {isExpanded ? 'Collapse' : 'Go Deeper'}
        </button>
        <div className="b-deep-dive__tags">
          {TABS.map(tab => {
            const isAvailable = availableTabs.some(t => t.id === tab.id);
            const isTabBuilding = isBuilding && buildingTab === tab.id;
            return (
              <span
                key={tab.id}
                className={`b-deep-dive__tag ${isAvailable ? 'b-deep-dive__tag--ready' : ''} ${isTabBuilding ? 'b-deep-dive__tag--building' : ''}`}
              >
                {tab.label}
                {isTabBuilding && <span className="b-building-dot-tiny" />}
              </span>
            );
          })}
        </div>
      </div>

      {/* Expandable content */}
      {isExpanded && (
        <div className="b-deep-dive__content" id="b-deep-content">
          {/* Tabs */}
          <div className="b-deep-dive__tabs">
            {availableTabs.map(tab => {
              const isTabBuilding = isBuilding && buildingTab === tab.id;
              return (
                <button
                  key={tab.id}
                  className={`b-deep-dive__tab ${activeTab === tab.id ? 'b-deep-dive__tab--active' : ''} ${isTabBuilding ? 'b-deep-dive__tab--building' : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                  {isTabBuilding && <span className="b-building-dot-small" />}
                </button>
              );
            })}
          </div>

          {/* Tab content */}
          <div className="b-deep-dive__tab-content">
            {activeTab === 'genre' && (
              <GenreTab report={report} onArtistClick={onArtistClick} />
            )}
            {activeTab === 'scenes' && (
              <ScenesTab report={report} onArtistClick={onArtistClick} />
            )}
            {activeTab === 'mutations' && (
              <MutationsTab report={report} onArtistClick={onArtistClick} />
            )}
            {activeTab === 'more' && (
              <MoreArtistsTab report={report} artists={artists} onArtistClick={onArtistClick} />
            )}
            {activeTab === 'infrastructure' && (
              <InfrastructureTab report={report} />
            )}
            {activeTab === 'coda' && (
              <CodaTab report={report} />
            )}
            {activeTab === 'reference' && (
              <ReferenceTab report={report} />
            )}
          </div>
        </div>
      )}
    </section>
  );
}

// Convert ReportArtist to BArtistCard format
function reportArtistToCard(artist: ReportArtist) {
  const tracks: string[] = [];
  if (artist.featuredTrack?.name) tracks.push(artist.featuredTrack.name);
  if (artist.otherTracks) tracks.push(...artist.otherTracks);

  return {
    name: artist.name,
    description: artist.whyTheyMatter || artist.story,
    location: artist.location || undefined,
    tracks,
    youtubeVideoId: artist.verifiedYoutubeId || artist.featuredTrack?.youtubeVideoId || undefined,
    bandcampEmbed: artist.verifiedBandcampEmbed,
    links: artist.links as Record<string, string | undefined>,
  };
}

function GenreTab({ report, onArtistClick }: { report: Report | null; onArtistClick?: (name: string) => void }) {
  const founders = report?.founders;
  const founderArtists = founders?.founders || [];
  const parentGenres = founders?.parentGenres || [];

  return (
    <div className="b-prose">
      {/* Introduction */}
      {report?.introduction?.content && (
        <>
          <h3>{report.introduction.sectionTitle || 'Origins'}</h3>
          {report.introduction.content.split('\n\n').map((para, i) => (
            <p key={i}>{para}</p>
          ))}
        </>
      )}

      {/* Parent Genres */}
      {parentGenres.length > 0 && (
        <div className="b-parent-genres">
          <h3>The Roots</h3>
          <div className="b-genre-cards">
            {parentGenres.map((genre, i) => (
              <div key={i} className="b-genre-card">
                <h4>{genre.name}</h4>
                <p>{genre.description}</p>
                {genre.keyArtist && (
                  <span className="b-key-artist">
                    Key Artist: <BClickableArtistName name={genre.keyArtist} />
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Lineage */}
      {founders?.lineageDescription && (
        <>
          <h3>{founders.title || 'The Founders'}</h3>
          <p>{founders.lineageDescription}</p>
        </>
      )}

      {/* Founder Artists */}
      {founderArtists.length > 0 && (
        <div className="b-tab-artists">
          {founderArtists.map((artist, i) => (
            <div key={i} className="b-artist-section">
              {/* Story content */}
              {(artist.story || artist.whyTheyMatter) && (
                <div className="b-artist-narrative">
                  {artist.story && artist.story.split('\n\n').map((para, j) => (
                    <p key={j}>{para}</p>
                  ))}
                  {artist.whyTheyMatter && (
                    <p className="b-why-matters"><strong>Why They Matter:</strong> {artist.whyTheyMatter}</p>
                  )}
                </div>
              )}
              <BArtistCard
                artist={reportArtistToCard(artist)}
                onArtistClick={onArtistClick}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ScenesTab({ report, onArtistClick }: { report: Report | null; onArtistClick?: (name: string) => void }) {
  const scenes = report?.scenes || [];

  if (scenes.length === 0) {
    return <div className="b-prose"><p>Scene information is being researched...</p></div>;
  }

  return (
    <div className="b-prose">
      {scenes.map((scene, i) => (
        <div key={scene.id || i} className="b-scene-section">
          <h3>{scene.title || 'Scene'}</h3>
          {scene.location && <span className="b-scene-location">{scene.location}</span>}
          {scene.description && scene.description.split('\n\n').map((para, j) => (
            <p key={j}>{para}</p>
          ))}
          {scene.whyHere && (
            <p className="b-why-here"><strong>Why Here:</strong> {scene.whyHere}</p>
          )}

          {scene.keyVenues && scene.keyVenues.length > 0 && (
            <div className="b-scene-meta">
              <span className="b-scene-meta-label">Key Venues:</span>
              {scene.keyVenues.map((venue, j) => (
                <span key={j} className="b-scene-meta-item">{venue}</span>
              ))}
            </div>
          )}

          {scene.keyLabels && scene.keyLabels.length > 0 && (
            <div className="b-scene-meta">
              <span className="b-scene-meta-label">Key Labels:</span>
              {scene.keyLabels.map((label, j) => (
                <span key={j} className="b-scene-meta-item">{label}</span>
              ))}
            </div>
          )}

          {scene.artists && scene.artists.length > 0 && (
            <div className="b-tab-artists">
              {scene.artists.map((artist, j) => (
                <div key={j} className="b-artist-section">
                  {(artist.story || artist.whyTheyMatter) && (
                    <div className="b-artist-narrative">
                      {artist.story && artist.story.split('\n\n').map((para, k) => (
                        <p key={k}>{para}</p>
                      ))}
                      {artist.whyTheyMatter && (
                        <p className="b-why-matters"><strong>Why They Matter:</strong> {artist.whyTheyMatter}</p>
                      )}
                    </div>
                  )}
                  <BArtistCard
                    artist={reportArtistToCard(artist)}
                    onArtistClick={onArtistClick}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function MutationsTab({ report, onArtistClick }: { report: Report | null; onArtistClick?: (name: string) => void }) {
  const mutations = report?.mutations || [];

  if (mutations.length === 0) {
    return <div className="b-prose"><p>Genre mutations are being researched...</p></div>;
  }

  return (
    <div className="b-mutations-container">
      {mutations.map((mutation, i) => {
        const artistCount = mutation.keyArtists?.length || 0;
        const isSingleArtist = artistCount === 1;

        return (
          <div key={mutation.id || i} className="b-mutation-section">
            <div className="b-mutation-header">
              <h3>{mutation.name}</h3>
              {mutation.soundDescription && (
                <p className="b-mutation-sound"><strong>The Sound:</strong> {mutation.soundDescription}</p>
              )}
            </div>

            <div className={`b-mutation-content ${isSingleArtist ? 'b-mutation-content--single' : ''}`}>
              {mutation.description && (
                <div className="b-mutation-description">
                  <p>{mutation.description}</p>
                </div>
              )}

              {mutation.keyArtists && artistCount > 0 && (
                <div className={`b-mutation-artists ${isSingleArtist ? 'b-mutation-artists--single' : ''}`}>
                  {mutation.keyArtists.map((artist, j) => (
                    <div key={j} className="b-mutation-artist-item">
                      <BArtistCard
                        artist={reportArtistToCard(artist)}
                        isFeatured={isSingleArtist}
                        onArtistClick={onArtistClick}
                      />
                      {(artist.story || artist.whyTheyMatter) && (
                        <div className="b-mutation-artist-narrative">
                          {artist.story && artist.story.split('\n\n').map((para, k) => (
                            <p key={k}>{para}</p>
                          ))}
                          {artist.whyTheyMatter && (
                            <p className="b-why-matters"><strong>Why They Matter:</strong> {artist.whyTheyMatter}</p>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function MoreArtistsTab({
  report,
  artists,
  onArtistClick,
}: {
  report: Report | null;
  artists: DiscoveredArtist[];
  onArtistClick?: (artistName: string) => void;
}) {
  // Report's more artists section
  const reportArtists = report?.moreArtists?.artists || [];
  // Discovered artists beyond the first 6
  const extraDiscovered = artists.slice(6);

  if (reportArtists.length === 0 && extraDiscovered.length === 0) {
    return <div className="b-prose"><p>More artists are being discovered...</p></div>;
  }

  return (
    <div className="b-prose">
      {report?.moreArtists?.sectionTitle && <h3>{report.moreArtists.sectionTitle}</h3>}
      {report?.moreArtists?.description && <p>{report.moreArtists.description}</p>}

      {/* Report artists with full narrative */}
      {reportArtists.length > 0 && (
        <div className="b-tab-artists">
          {reportArtists.map((artist, i) => (
            <div key={i} className="b-artist-section">
              {(artist.story || artist.whyTheyMatter) && (
                <div className="b-artist-narrative">
                  {artist.story && artist.story.split('\n\n').map((para, j) => (
                    <p key={j}>{para}</p>
                  ))}
                  {artist.whyTheyMatter && (
                    <p className="b-why-matters"><strong>Why They Matter:</strong> {artist.whyTheyMatter}</p>
                  )}
                </div>
              )}
              <BArtistCard
                artist={reportArtistToCard(artist)}
                onArtistClick={onArtistClick}
              />
            </div>
          ))}
        </div>
      )}

      {/* Discovered artists */}
      {extraDiscovered.length > 0 && (
        <>
          {reportArtists.length > 0 && (
            <h4 className="b-more-divider">More to Explore</h4>
          )}
          <div className="b-more-grid">
            {extraDiscovered.slice(0, 8).map((artist) => (
              <BArtistCard
                key={artist.name}
                artist={{
                  name: artist.name,
                  description: artist.matchReason,
                  location: artist.location || undefined,
                  tracks: artist.tracks,
                  youtubeVideoId: artist.youtubeVideoId,
                  bandcampEmbed: artist.bandcampEmbed,
                  links: artist.links,
                }}
                onArtistClick={onArtistClick}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function InfrastructureTab({ report }: { report: Report | null }) {
  const infra = report?.infrastructure;
  const labels = infra?.labels || [];
  const radio = infra?.radio || [];
  const festivals = infra?.festivals || [];

  if (labels.length === 0 && radio.length === 0 && festivals.length === 0) {
    return <div className="b-prose"><p>Infrastructure information is being researched...</p></div>;
  }

  return (
    <div className="b-prose">
      <h3>{infra?.sectionTitle || 'The DIY Infrastructure'}</h3>
      {infra?.description && <p>{infra.description}</p>}

      <div className="b-infra-grid">
        {labels.length > 0 && (
          <div className="b-infra-card">
            <h4>Labels That Matter</h4>
            {labels.map((entry, i) => (
              <InfraEntryItem key={i} entry={entry} />
            ))}
          </div>
        )}

        {radio.length > 0 && (
          <div className="b-infra-card">
            <h4>Radio & Discovery</h4>
            {radio.map((entry, i) => (
              <InfraEntryItem key={i} entry={entry} />
            ))}
          </div>
        )}

        {festivals.length > 0 && (
          <div className="b-infra-card">
            <h4>Festivals</h4>
            {festivals.map((entry, i) => (
              <InfraEntryItem key={i} entry={entry} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function InfraEntryItem({ entry }: { entry: InfrastructureEntry | string }) {
  if (typeof entry === 'string') {
    return <div className="b-infra-entry"><span className="b-infra-name">{entry}</span></div>;
  }

  return (
    <div className="b-infra-entry">
      <div className="b-infra-header">
        <strong className="b-infra-name">{entry.name}</strong>
        {entry.location && <span className="b-infra-location">{entry.location}</span>}
      </div>
      {entry.description && <p className="b-infra-description">{entry.description}</p>}
      {entry.whyItMatters && (
        <p className="b-infra-why"><strong>Why It Matters:</strong> {entry.whyItMatters}</p>
      )}
      {(entry.links?.website || entry.links?.bandcamp) && (
        <div className="b-infra-links">
          {entry.links.website && (
            <a href={entry.links.website} target="_blank" rel="noopener noreferrer">Website</a>
          )}
          {entry.links.bandcamp && (
            <a href={entry.links.bandcamp} target="_blank" rel="noopener noreferrer">Bandcamp</a>
          )}
        </div>
      )}
    </div>
  );
}

function CodaTab({ report }: { report: Report | null }) {
  const coda = report?.coda;

  if (!coda?.content) {
    return <div className="b-prose"><p>The story continues...</p></div>;
  }

  return (
    <div className="b-prose">
      <h3>{coda.sectionTitle || 'Why This Matters'}</h3>
      {coda.content.split('\n\n').map((para, i) => (
        <p key={i}>{para}</p>
      ))}
    </div>
  );
}

// Quick Reference Row with enriched data
function QuickRefRow({ entry }: { entry: { artistName: string; genreStyle?: string; location?: string | null; bandcamp?: string; spotify?: string; apple?: string } }) {
  const { data: artistData } = useArtistData(entry.artistName);

  const links: AnyLinks = {
    ...(artistData?.links || {}),
    bandcamp: artistData?.links?.bandcamp || entry.bandcamp,
    spotify: artistData?.links?.spotify || entry.spotify,
    apple: artistData?.links?.apple || entry.apple,
  };

  return (
    <div className="b-ref-row">
      <span className="b-ref-artist">
        <BClickableArtistName name={entry.artistName} />
      </span>
      <span className="b-ref-style">{entry.genreStyle || '—'}</span>
      <span className="b-ref-location">{entry.location || '—'}</span>
      <span className="b-ref-links">
        <PlatformLinks
          links={links}
          artistName={entry.artistName}
          size="small"
          showSearchFallbacks={true}
        />
      </span>
    </div>
  );
}

function ReferenceTab({ report }: { report: Report | null }) {
  const quickRef = report?.quickReference || [];

  if (quickRef.length === 0) {
    return <div className="b-prose"><p>Reference data is being compiled...</p></div>;
  }

  return (
    <div className="b-reference-table">
      <div className="b-ref-header">
        <span>Artist</span>
        <span>Genre/Style</span>
        <span>Location</span>
        <span>Links</span>
      </div>
      {quickRef.map((entry, i) => (
        <QuickRefRow key={i} entry={entry} />
      ))}
    </div>
  );
}
